import { Dispatch, FC, SetStateAction, useEffect, useState } from "react";

import { Col, Checkbox } from "antd";
import UkCustomFormItem from "components/basic/FormWrapper/UkFormItemWrapper";
import UkFloatInput from "components/inputs/Form/Inputs/UkFloatInput";
import { Link } from "react-router-dom";
import zxcvbn from "zxcvbn";

import { UK_ROUTES } from "../../../constants/routes";
import { EnableValidationT } from "../SignUpForm/SignUpFormNew";
import "styles/signatureInput.less";

type SignDataType = {
    [key: string]: boolean | Blob | null | string;
    termsAgreed: boolean;
    signature: Blob | null;
    referralCode: string;
    password: string;
};

interface SignInputsProps {
    setSignData: Dispatch<SetStateAction<SignDataType>>;
    isCreateAccountChecked: boolean;
    setIsCreateAccountChecked: Dispatch<SetStateAction<boolean>>;
    setEnableValidation: Dispatch<SetStateAction<EnableValidationT>>;
    passwordError: boolean;
    setPasswordError: Dispatch<SetStateAction<boolean>>;
    inputId: string;
    handleKeyDown?: (_event: any) => void;
}

const SignInputs: FC<SignInputsProps> = ({
    setSignData,
    setPasswordError,
    setEnableValidation,
    inputId,
    handleKeyDown,
}) => {
    const [password, setPassword] = useState("");
    const [passwordScore, setPasswordScore] = useState(0);
    const [passwordFeedback, setPasswordFeedback] = useState({
        warning: "",
        suggestions: [] as string[],
    });

    const [showPassword, setShowPassword] = useState(false);

    const togglePasswordVisibility = () => setShowPassword(prev => !prev);

    const strengthLabels = ["Too Weak", "Weak", "Fair", "Strong", "Very Strong"];
    const strengthColors = ["#ff4d4f", "#ff7a45", "#faad14", "#52c41a", "#389e0d"];

    const handleInputChange = (field: string, e: any) => {
        const newValue = e.target.value;

        if (field === "password") {
            setPassword(newValue);

            const result = zxcvbn(newValue);
            setPasswordScore(result.score);
            setPasswordFeedback({
                warning: result.feedback.warning || "",
                suggestions: result.feedback.suggestions || [],
            });

            setPasswordError(result.score < 3);
        }

        setSignData(prev => ({
            ...prev,
            [field]: newValue,
        }));
    };

    const handleTermsCheckChange = (e: any) => {
        setSignData(prev => ({
            ...prev,
            termsAgreed: e.target.checked,
        }));
    };

    useEffect(() => {
        setEnableValidation(prev => ({ ...prev, [inputId]: true }));
    }, [inputId, setEnableValidation]);

    return (
        <Col>
            <UkCustomFormItem>
                <UkFloatInput
                    label="Password"
                    dataTestId="password"
                    value={password}
                    type={showPassword ? "text" : "password"}
                    onChange={e => handleInputChange("password", e)}
                    handleKeyDown={handleKeyDown}
                    inputId={inputId}
                    showPassword={showPassword}
                    togglePasswordVisibility={togglePasswordVisibility}
                />
            </UkCustomFormItem>

            {/* Password Strength Indicator */}
            {password && (
                <div className="mb8">
                    <div
                        style={{
                            background: strengthColors[passwordScore],
                            width: `${(passwordScore + 1) * 20}%`,
                        }}
                        className="signature-input__strength-bar"
                    />
                    <div
                        style={{
                            color: strengthColors[passwordScore],
                        }}
                        className="signature-input__strength-label"
                    >
                        {strengthLabels[passwordScore]}
                    </div>
                </div>
            )}

            {/* Feedback Text */}
            {(passwordFeedback.warning || passwordFeedback.suggestions.length > 0) && (
                <div className="signature-input__feedback">
                    {passwordFeedback.warning && (
                        <div className="signature-input__feedback-warning">
                            ⚠️ {passwordFeedback.warning}
                        </div>
                    )}
                    {passwordFeedback.suggestions.map((s, i) => (
                        <div key={i}>• {s}</div>
                    ))}
                </div>
            )}

            <Checkbox
                defaultChecked
                disabled={passwordScore < 3}
                className="input-item"
                onChange={handleTermsCheckChange}
            >
                By continuing you accept the{" "}
                <Link to={UK_ROUTES.tcs} className="email-link" target="_blank" rel="noreferrer">
                    Terms & Conditions
                </Link>{" "}
                and{" "}
                <Link to={UK_ROUTES.engagement} className="email-link" target="_blank" rel="noreferrer">
                    Terms of Engagement
                </Link>
            </Checkbox>
        </Col>
    );
};

export default SignInputs;
